// Client-side wrappers for the credits / models edge functions and tables.
import { supabase } from "@/integrations/supabase/client";

export type TaskType = "rewrite" | "image" | "translate" | "qa" | "seo" | "score" | "embed";

export type ModelEntry = {
  key: string;
  label: string;
  description: string | null;
  provider: string;
  credits_cost: number;
  quality_tier: string | null;
  speed_tier: string | null;
  supports_vision: boolean;
  locked: boolean;
  is_default?: boolean;
  required_tier_sort_order?: number;
  required_package_name?: string | null;
  upgrade_url?: string;
};

export type AvailableModelsResponse = {
  tier_sort_order: number;
  default_model_key: string | null;
  models: ModelEntry[];
};

export type LockedModelsResponse = {
  tier_sort_order: number;
  models: ModelEntry[];
};

export type CreditQuoteResponse = {
  model_key: string;
  label: string;
  quality_tier: string | null;
  speed_tier: string | null;
  credits_cost: number;
  balance_after_if_charged: number;
  can_afford: boolean;
  resolved_via: "request_override" | "site_pref" | "org_pref" | "system_default";
  alternative_models: Array<{
    key: string;
    label: string;
    credits_cost: number;
    quality_tier: string | null;
    speed_tier: string | null;
  }>;
};

async function invoke<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>(name, { body });
  if (error) throw error;
  if (!data) throw new Error(`${name} returned no data`);
  return data;
}

export const listAvailableModels = (orgId: string, taskType: TaskType, siteId?: string | null) =>
  invoke<AvailableModelsResponse>("list-available-models", { orgId, taskType, siteId: siteId ?? null });

export const listLockedModels = (orgId: string, taskType: TaskType) =>
  invoke<LockedModelsResponse>("list-locked-models", { orgId, taskType });

export const creditQuote = (orgId: string, taskType: TaskType, siteId?: string | null, modelKey?: string | null) =>
  invoke<CreditQuoteResponse>("credit-quote", { orgId, taskType, siteId: siteId ?? null, modelKey: modelKey ?? null });

// Wallet / usage queries — direct table reads under RLS.
// deno-lint-ignore no-explicit-any
const db = supabase as any;

export type Wallet = {
  balance: number;
  lifetime_granted: number;
  lifetime_consumed: number;
  updated_at: string;
};

export async function getWallet(orgId: string): Promise<Wallet | null> {
  const { data, error } = await db
    .from("credit_wallets")
    .select("balance, lifetime_granted, lifetime_consumed, updated_at")
    .eq("company_id", orgId)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

export type UsageRow = {
  id: string;
  task_type: string;
  model_key: string | null;
  resolved_via: string | null;
  credits_charged: number;
  provider_cost_micros: number | null;
  status: string;
  created_at: string;
  article_id: string | null;
};

export async function getUsageEvents(orgId: string, limit = 200): Promise<UsageRow[]> {
  const { data, error } = await db
    .from("usage_events")
    .select("id, task_type, model_key, resolved_via, credits_charged, provider_cost_micros, status, created_at, article_id")
    .eq("company_id", orgId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

export type MonthlyAllowance = {
  monthly_credits: number;
  resets_at: string | null;
};

// Read monthly_credits from package_features.feature_value for the org's
// active subscription. Returns 0 if no allowance configured.
export async function getMonthlyAllowance(orgId: string): Promise<MonthlyAllowance> {
  const { data: sub } = await db
    .from("user_subscriptions")
    .select("package_id, current_period_end")
    .eq("company_id", orgId)
    .in("status", ["active", "trialing"])
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (!sub?.package_id) return { monthly_credits: 0, resets_at: null };

  const { data: feat } = await db
    .from("package_features")
    .select("feature_value")
    .eq("package_id", sub.package_id)
    .eq("feature_key", "monthly_credits")
    .maybeSingle();
  const fv = (feat?.feature_value ?? {}) as { amount?: number };
  return { monthly_credits: Number(fv.amount ?? 0), resets_at: sub.current_period_end ?? null };
}

export type CreditPack = {
  id: string;
  sku: string;
  credits: number;
  price_cents: number;
  currency: string;
  sort_order: number;
};

export async function getCreditPacks(): Promise<CreditPack[]> {
  const { data, error } = await db
    .from("credit_packs")
    .select("id, sku, credits, price_cents, currency, sort_order")
    .eq("active", true)
    .order("sort_order");
  if (error) throw error;
  return data ?? [];
}

// Preference writes — used by the model picker at site / org scope.
export async function upsertSitePreference(websiteId: string, taskType: TaskType, modelKey: string | null, fallbackModelKey: string | null) {
  if (!modelKey) {
    // Reset — delete the row so resolver falls through to org / system default.
    const { error } = await db.from("website_ai_preferences").delete().eq("website_id", websiteId).eq("task_type", taskType);
    if (error) throw error;
    return;
  }
  const { error } = await db
    .from("website_ai_preferences")
    .upsert({ website_id: websiteId, task_type: taskType, preferred_model_key: modelKey, fallback_model_key: fallbackModelKey, updated_at: new Date().toISOString() });
  if (error) throw error;
}

export async function upsertOrgPreference(orgId: string, taskType: TaskType, modelKey: string | null) {
  if (!modelKey) {
    const { error } = await db.from("company_ai_preferences").delete().eq("company_id", orgId).eq("task_type", taskType);
    if (error) throw error;
    return;
  }
  const { error } = await db
    .from("company_ai_preferences")
    .upsert({ company_id: orgId, task_type: taskType, preferred_model_key: modelKey, updated_at: new Date().toISOString() });
  if (error) throw error;
}

export async function getSitePreferences(websiteId: string) {
  const { data, error } = await db
    .from("website_ai_preferences")
    .select("task_type, preferred_model_key, fallback_model_key")
    .eq("website_id", websiteId);
  if (error) throw error;
  return (data ?? []) as Array<{ task_type: string; preferred_model_key: string | null; fallback_model_key: string | null }>;
}

export async function getOrgPreferences(orgId: string) {
  const { data, error } = await db
    .from("company_ai_preferences")
    .select("task_type, preferred_model_key")
    .eq("company_id", orgId);
  if (error) throw error;
  return (data ?? []) as Array<{ task_type: string; preferred_model_key: string | null }>;
}
